import { useState, useRef, useCallback } from 'react';

// Custom hook for smooth progress management
export const useProgress = () => {
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);
  const animationRef = useRef(null);
  const targetRef = useRef(0);

  // Smooth progress update function
  const updateProgress = useCallback((targetProgress, duration = 1000) => {
    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    // Ensure we never go backwards
    targetProgress = Math.max(progressRef.current, targetProgress);
    targetRef.current = targetProgress;

    const startTime = Date.now();
    const startProgress = progressRef.current;
    const progressDiff = targetProgress - startProgress;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const ratio = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easedRatio = 1 - Math.pow(1 - ratio, 3); // Cubic ease-out

      const currentProgress = startProgress + progressDiff * easedRatio;
      progressRef.current = currentProgress;
      setProgress(Math.round(currentProgress));

      if (ratio < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, []);

  // Reset progress
  const resetProgress = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    progressRef.current = 0;
    targetRef.current = 0;
    setProgress(0);
  }, []);

  // Get current progress without state update
  const getCurrentProgress = useCallback(() => progressRef.current, []);

  // Check if progress is complete
  const isComplete = progress >= 100;

  return {
    progress,
    updateProgress,
    resetProgress,
    getCurrentProgress,
    isComplete,
  };
};

// Progress milestones for different stages
export const ANALYSIS_MILESTONES = [
  { progress: 0, message: 'Starting analysis...', duration: 1000 },
  { progress: 15, message: 'Analyzing your website...', duration: 1500 },
  { progress: 35, message: 'Assessing your core offerings...', duration: 2000 },
  { progress: 55, message: 'Understanding your typical customers...', duration: 1800 },
  { progress: 75, message: 'Evaluating your business model...', duration: 2000 },
  { progress: 90, message: 'Analyzing market position...', duration: 1500 },
  { progress: 100, message: 'Finalizing business analysis...', duration: 800 },
];

export const VALUATION_MILESTONES = [
  { progress: 0, message: 'Starting valuation...', duration: 1000 },
  { progress: 25, message: 'Applying industry multiples...', duration: 1800 },
  { progress: 50, message: 'Analyzing market comparables...', duration: 2000 },
  { progress: 75, message: 'Calculating business value...', duration: 1800 },
  { progress: 90, message: 'Finalizing valuation report...', duration: 1500 },
  { progress: 100, message: 'Preparing your personalized report...', duration: 800 },
];

// Fun facts to display during loading
export const BUSINESS_FACTS = [
  'The average small business sells for 2-4x annual profits.',
  'Businesses with recurring revenue typically get 30-50% higher valuations.',
  'Having clean financial records can increase your sale price by 10-20%.',
  "The best time to sell is when you don't have to - you'll negotiate better.",
  'Most business sales take 6-12 months from start to finish.',
  'Buyers pay premium prices for businesses with growth potential.',
  'Having multiple revenue streams increases your business value.',
  'Customer concentration matters - diversified revenue is more valuable.',
];
